"use client";

import { useEffect, useState } from "react";
import { Skeleton, WidgetSection } from "@dionatanbig2be/big2be-ui";
import { cn } from "@dionatanbig2be/big2be-ui/utils";
import { ProductCard, type ProductCardModel } from "@/modules/catalog";

/**
 * Cache da sessão por lista de ids: abrir de novo o mesmo departamento (ou
 * passar o mouse de novo na aba) não refaz a busca.
 */
const cache = new Map<string, Promise<ProductCardModel[]>>();

function fetchPanelProducts(ids: number[]): Promise<ProductCardModel[]> {
    const key = ids.join(",");
    let request = cache.get(key);
    if (!request) {
        request = fetch(`/api/catalog/products?ids=${key}`)
            .then((response) => response.json() as Promise<{ products: ProductCardModel[] }>)
            .then((data) => data.products)
            .catch(() => {
                cache.delete(key); // falha não fica cacheada: a próxima abertura tenta de novo.
                return [];
            });
        cache.set(key, request);
    }
    return request;
}

interface CategoryPanelProductsProps {
    productIds: number[];
    /** `column`: coluna em pé do desktop (no lugar da arte retrato); `strip`: faixa do celular. */
    layout: "column" | "strip";
    /** Fecha o painel quando um card leva à PDP — como o link da arte faz. */
    onNavigate?: () => void;
    /** O espaço da vitrine (o mesmo que a arte ocuparia) — some junto quando não há produto. */
    className?: string;
}

/**
 * Produtos em destaque do painel de departamentos — o carrossel que ocupa o
 * lugar da imagem quando a categoria foi configurada como vitrine no portal.
 * Busca ao abrir o painel (não na árvore de categorias, que toda página da loja
 * carrega) e mapeia no servidor, pela rota `/api/catalog/products`.
 */
export function CategoryPanelProducts({
    productIds,
    layout,
    onNavigate,
    className,
}: CategoryPanelProductsProps) {
    const key = productIds.join(",");
    const [loaded, setLoaded] = useState<{ key: string; products: ProductCardModel[] } | null>(null);

    useEffect(() => {
        let cancelled = false;
        void fetchPanelProducts(productIds).then((products) => {
            if (!cancelled) setLoaded({ key, products });
        });
        return () => {
            cancelled = true;
        };
        // `key` é a identidade da lista — o array em si muda de referência a cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // Desktop: o mesmo card da vitrine da home (variant-3 em `md:w-52`, ver
    // ProductCollection). Faixa do celular: cards menores, vários por tela.
    const itemClassName = layout === "column" ? "w-52" : "w-36";

    if (loaded?.key !== key) {
        return (
            <div data-component="CategoryPanelProducts" className={className}>
                <div className="flex gap-3 overflow-hidden">
                    {[0, 1].map((i) => (
                        <Skeleton key={i} className={cn("h-64 shrink-0 rounded-box", itemClassName)} />
                    ))}
                </div>
            </div>
        );
    }

    // Nenhum produto vendável na loja: nem o espaço fica — como sem imagem.
    if (loaded.products.length === 0) return null;

    const cards = loaded.products.map((product) => (
        <div key={product.productId} className={cn("shrink-0", itemClassName)}>
            <ProductCard product={product} variant="variant-3" block disableQuantityStepper />
        </div>
    ));

    return (
        <div data-component="CategoryPanelProducts" className={className}>
            {/* Clique em link (PDP) fecha o painel; o botão de comprar não — o
                cliente pode adicionar vários sem perder o menu. */}
            <div
                className="min-w-0"
                onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a")) onNavigate?.();
                }}
            >
                {/* Um produto só: não há o que rolar, então sem trilho e sem setas.
                    Com mais, o mesmo trilho dos widgets de produto (ProductCollection):
                    WidgetSection em `scroll` + ProductCard `block`, que obedece à
                    largura do slot. Sem título, as setas flutuam sobre o trilho. */}
                {cards.length === 1 ? (
                    cards
                ) : (
                    <WidgetSection
                        componentName="CategoryPanelProductsRail"
                        layout="scroll"
                        navAlign="center"
                        navSize="xs"
                        edge="none"
                        scrollStep="page"
                        className="py-0"
                    >
                        {cards}
                    </WidgetSection>
                )}
            </div>
        </div>
    );
}
